import {
  APPRAISIFY_ADDRESS,
  APPRAISIFY_NAME,
  AloftDate,
  AloftDateJSON,
  AloftPriceJSON,
  formatAppraisalType,
  noop,
} from '@aloft/toolkit-shared';
import { Block, Flex, Typography } from '@aloft/toolkit-sui';
// eslint-disable-next-line aloft/no-sui-chakra
import { Divider } from '@aloft/toolkit-sui/chakra';

import { Form, FormProvider, SubmitErrorHandler, SubmitHandler } from 'react-hook-form';

import { useZodForm } from '../../../hooks/useZodForm.ts';
import { useAppraisifyValidation } from '../../../hooks/validation/useAppraisifyValidation.ts';
import { submitEditOrderDetailsForm } from '../../../redux/features/actions/submitEditOrderDetailsForm.ts';
import { selectOrder } from '../../../redux/features/selectors/orderSelectors.ts';
import { selectOrderDetailsFormValues } from '../../../redux/features/selectors/selectOrderDetailsFormValues.ts';
import { selectReconciliation } from '../../../redux/features/selectors/selectReconciliation.ts';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks.ts';
import { OrderDetailsInput } from '../components/OrderDetailsInput.tsx';
import { OrderDetailsFormOutput, schOrderDetailsForm } from '../schemas.ts';

// Component that calls validation hook from within form context
export const OrderDetails = () => {
  const validation = useAppraisifyValidation();
  const { effectiveDate, intendedUsers, intendedUse } = validation.errors.OrderDetails;
  const { totalRenovationBudget: totalRenovationBudgetError } = validation.errors.Reconciliation;

  const dispatch = useAppDispatch();
  const defaultValues = useAppSelector(selectOrderDetailsFormValues);

  const order = useAppSelector(selectOrder);
  const { includeTotalRenovationBudget } = useAppSelector(selectReconciliation);

  const form = useZodForm({
    schema: schOrderDetailsForm,
    defaultValues,
    mode: 'onBlur',
    reValidateMode: 'onBlur',
    shouldFocusError: false, // Prevent focus jumping on validation errors
  });

  const onSubmit: SubmitHandler<OrderDetailsFormOutput> = (data) => {
    dispatch(
      submitEditOrderDetailsForm({
        effectiveDate: data.effectiveDate,
        intendedUsers: data.intendedUsers,
        intendedUse: data.intendedUse,
        totalRenovationBudget: data.totalRenovationBudget,
        id: 'subject',
      }),
    );
  };

  const onError: SubmitErrorHandler<{
    effectiveDate?: AloftDateJSON;
    intendedUsers?: string;
    intendedUse?: string;
    totalRenovationBudget?: AloftPriceJSON;
  }> = (errors) => console.error(errors);

  const handleChange = () => {
    form.handleSubmit(onSubmit, onError)().catch(noop);
  };

  return (
    <FormProvider {...form}>
      <Form onChange={handleChange}>
        <Flex flexDirection="column" padding="15px">
          <Typography variant="h2">Order Details</Typography>
          <Divider
            borderColor="strokeDefault"
            marginY="10px"
            borderTopWidth="1px"
            borderBottomWidth="0px"
          />
          <Flex gap="15px" lineHeight="32px" flexDirection="column">
            <Flex>
              <Block width="250px">
                <Typography variant="h4" color="textSecondary">
                  Client Name
                </Typography>
              </Block>
              <Typography variant="body1" color="textPrimary">
                {order.appraisifyName ?? APPRAISIFY_NAME}
              </Typography>
            </Flex>
            <Flex>
              <Block width="250px">
                <Typography variant="h4" color="textSecondary">
                  Client Address
                </Typography>
              </Block>
              <Typography variant="body1" color="textPrimary">
                {order.appraisifyAddress ?? APPRAISIFY_ADDRESS}
              </Typography>
            </Flex>
            <Flex>
              <Block width="250px">
                <Typography variant="h4" color="textSecondary">
                  Borrower
                </Typography>
              </Block>
              <Typography variant="body1" color="textPrimary">
                {order.borrowerName || 'N/A'}
              </Typography>
            </Flex>
            <Flex>
              <Block width="250px">
                <Typography variant="h4" color="textSecondary">
                  Product Type
                </Typography>
              </Block>
              <Typography variant="body1" color="textPrimary">
                {order.appraisalType ? formatAppraisalType(order.appraisalType) : 'N/A'}
              </Typography>
            </Flex>
            <Flex>
              <Block width="250px">
                <Typography variant="h4" color="textSecondary">
                  Inspection Date
                </Typography>
              </Block>
              <Typography variant="body1" color="textPrimary">
                {order.inspectionDate
                  ? AloftDate.schema.parse(order.inspectionDate).toString()
                  : 'N/A'}
              </Typography>
            </Flex>
            <OrderDetailsInput
              control={form.control}
              name="effectiveDate"
              label="Effective Date"
              isRequired
              type="aloftDate"
              errorMessage={effectiveDate?.message}
            />
            <OrderDetailsInput
              control={form.control}
              name="intendedUsers"
              label="Intended User(s)"
              isRequired
              placeholderText="Enter intended user(s)"
              errorMessage={intendedUsers?.message}
            />
            <OrderDetailsInput
              control={form.control}
              name="intendedUse"
              label="Intended Use"
              isRequired
              placeholderText="Enter intended use"
              errorMessage={intendedUse?.message}
            />
            {includeTotalRenovationBudget && (
              <OrderDetailsInput
                control={form.control}
                name="totalRenovationBudget"
                label="Renovation Budget"
                isRequired
                type="aloftPrice"
                placeholderText="Enter renovation budget"
                errorMessage={totalRenovationBudgetError?.message}
              />
            )}
          </Flex>
        </Flex>
      </Form>
    </FormProvider>
  );
};
